import { makeAutoObservable, runInAction } from "mobx";
import { validate } from "class-validator";
import type {
  LegalFormData,
  RawContractorData,
  RawLegalData,
} from "../legal-types";
import { INITIAL_FORM } from "../legal-types";
import {
  LegalStep1Dto,
  LegalStep2Dto,
  LegalStep3Dto,
  LegalStep4Dto,
  SelfEmployedDto,
} from "../legal-dto";

// ─── Форм-стор: форма, ошибки валидации, prefill из API ──────────────────────

/** Маппинг поля формы → номер шага (для отслеживания dirty) */
const FIELD_STEP_MAP: Record<keyof LegalFormData, number> = {
  // Шаг 1 — Реквизиты
  orgSearch: 0,
  legalName: 1,
  legalFullName: 1,
  inn: 1,
  ogrn: 1,
  kpp: 1,
  orgForm: 1,
  billingDescriptor: 1,
  legalEmail: 1,
  siteUrl: 1,
  // Шаг 2 — Адрес
  addressSearch: 0,
  legalCountry: 2,
  city: 2,
  street: 2,
  building: 2,
  office: 2,
  postalCode: 2,
  // Шаг 3 — Директор
  directorLastName: 3,
  directorFirstName: 3,
  directorMiddleName: 3,
  directorPhone: 3,
  directorCitizenship: 3,
  // Шаг 4 — Банк
  bankSearch: 0,
  bankName: 4,
  bik: 4,
  corrAccount: 4,
  settlementAccount: 4,
  bankPaymentDetails: 4,
  // Самозанятый (нет шагов 1-4)
  selfFullName: 0,
  selfInn: 0,
  selfPhone: 0,
  selfPassportSeries: 0,
  selfPassportNumber: 0,
  selfPassportIssuedBy: 0,
  selfPassportDate: 0,
};

export class LegalFormStore {
  form: LegalFormData = { ...INITIAL_FORM };
  validationErrors: Record<string, string> = {};
  /** Шаги, изменённые пользователем после последнего сохранения */
  dirtySteps: Set<number> = new Set();

  constructor() {
    makeAutoObservable(this);
  }

  setField = (field: keyof LegalFormData, value: string) => {
    this.form[field] = value;
    const step = FIELD_STEP_MAP[field];
    if (step > 0) this.dirtySteps.add(step);
    if (this.validationErrors[field]) {
      delete this.validationErrors[field];
    }
  };

  setFormBatch = (data: Partial<LegalFormData>) => {
    this.form = { ...this.form, ...data };
    // Пометить все затронутые шаги как dirty (DaData suggestion)
    for (const key of Object.keys(data) as (keyof LegalFormData)[]) {
      const step = FIELD_STEP_MAP[key];
      if (step > 0) this.dirtySteps.add(step);
    }
    this.validationErrors = {};
  };

  markStepClean = (step: number) => {
    this.dirtySteps.delete(step);
  };

  prefillFromLegal = (d: RawLegalData) => {
    const parts = (d.legal_street ?? "").split(", ");
    const [street, building] =
      parts.length >= 2
        ? [parts.slice(0, -1).join(", "), parts.slice(-1)[0]]
        : [d.legal_street ?? "", ""];

    this.form = {
      ...INITIAL_FORM,
      legalName: d.legal_name ?? "",
      legalFullName: d.legal_full_name ?? "",
      inn: d.inn ?? "",
      kpp: d.kpp ?? "",
      ogrn: d.ogrn ?? "",
      orgForm: d.orgForm ?? "",
      billingDescriptor: d.billing_descriptor ?? "",
      legalEmail: d.legal_email ?? "",
      siteUrl: d.site_url ?? "",
      legalCountry: d.legal_country ?? "RUS",
      city: d.legal_city ?? "",
      street,
      building,
      office: d.office ?? "",
      postalCode: d.legal_zip ?? "",
      directorLastName: d.director_last_name ?? "",
      directorFirstName: d.director_first_name ?? "",
      directorMiddleName: d.director_middle_name ?? "",
      directorPhone: d.director_phone ?? "",
      directorCitizenship: d.director_citizenship ?? "RUS",
      bankName: d.bank_name ?? "",
      bik: d.bik ?? "",
      corrAccount: d.correspondent_account ?? "",
      settlementAccount: d.settlement_account ?? "",
      bankPaymentDetails: d.bank_payment_details ?? "",
    };
  };

  prefillFromContractor = (d: RawContractorData) => {
    this.form = {
      ...INITIAL_FORM,
      selfFullName: d.contractor_full_name ?? "",
      selfInn: d.contractor_inn ?? "",
      selfPhone: d.contractor_phone ?? "",
      selfPassportSeries: d.contractor_passport_series ?? "",
      selfPassportNumber: d.contractor_passport_number ?? "",
      selfPassportIssuedBy: d.contractor_passport_issued_by ?? "",
      selfPassportDate: d.contractor_passport_issue_date ?? "",
    };
  };

  resetForm = () => {
    this.form = { ...INITIAL_FORM };
    this.validationErrors = {};
    this.dirtySteps = new Set();
  };

  clearErrors = () => {
    this.validationErrors = {};
  };

  validateStep = async (step: number): Promise<boolean> => {
    const f = this.form;
    let dto: LegalStep1Dto | LegalStep2Dto | LegalStep3Dto | LegalStep4Dto;

    if (step === 1) {
      dto = Object.assign(new LegalStep1Dto(), {
        legalName: f.legalName.trim(),
        legalFullName: f.legalFullName.trim(),
        inn: f.inn.trim(),
        ogrn: f.ogrn.trim(),
        kpp: f.kpp.trim(),
        orgForm: f.orgForm,
        billingDescriptor: f.billingDescriptor.trim(),
        legalEmail: f.legalEmail.trim(),
        siteUrl: f.siteUrl.trim(),
      });
    } else if (step === 2) {
      dto = Object.assign(new LegalStep2Dto(), {
        city: f.city.trim(),
        street: f.street.trim(),
        building: f.building.trim(),
        postalCode: f.postalCode.trim(),
      });
    } else if (step === 3) {
      dto = Object.assign(new LegalStep3Dto(), {
        directorLastName: f.directorLastName.trim(),
        directorFirstName: f.directorFirstName.trim(),
        directorPhone: f.directorPhone.trim(),
      });
    } else {
      dto = Object.assign(new LegalStep4Dto(), {
        bankName: f.bankName.trim(),
        bik: f.bik.trim(),
        corrAccount: f.corrAccount.trim(),
        settlementAccount: f.settlementAccount.trim(),
      });
    }

    const errors = await validate(dto);
    const errMap: Record<string, string> = {};
    for (const err of errors) {
      if (err.constraints)
        errMap[err.property] = Object.values(err.constraints)[0];
    }
    runInAction(() => {
      this.validationErrors = errMap;
    });
    return errors.length === 0;
  };

  validateSelfEmployed = async (): Promise<boolean> => {
    const f = this.form;
    const dto = Object.assign(new SelfEmployedDto(), {
      selfFullName: f.selfFullName.trim(),
      selfInn: f.selfInn.trim(),
      selfPhone: f.selfPhone.trim(),
      selfPassportSeries: f.selfPassportSeries.trim(),
      selfPassportNumber: f.selfPassportNumber.trim(),
    });
    const errors = await validate(dto);
    const errMap: Record<string, string> = {};
    for (const err of errors) {
      if (err.constraints)
        errMap[err.property] = Object.values(err.constraints)[0];
    }
    runInAction(() => {
      this.validationErrors = errMap;
    });
    return errors.length === 0;
  };
}
